/**
 * Schema Utilities
 * Shared functions and pre-extracted defaults from JSON Schemas
 */

import journeySchema from '@forest/database/schemas/journey.schema.json';
import themeSchema from '@forest/database/schemas/theme.schema.json';
import type { ThemeLanguage } from '@/lib/types/brand-config';

/**
 * Extract default value from a JSON Schema property path
 */
export function getSchemaDefault<T>(schema: Record<string, unknown>, path: string[]): T | undefined {
  let current: unknown = schema;
  for (const key of path) {
    if (current && typeof current === 'object' && 'properties' in current) {
      current = (current as Record<string, unknown>).properties;
    }
    if (current && typeof current === 'object' && key in current) {
      current = (current as Record<string, unknown>)[key];
    } else {
      return undefined;
    }
  }
  if (current && typeof current === 'object' && 'default' in current) {
    return (current as { default: T }).default;
  }
  return undefined;
}

/**
 * Compute delta between current config and inherited config
 * Returns only the fields that differ from inherited values
 * Used to save only scope-specific overrides, not inherited values
 */
export function computeDelta<T extends object>(inherited: T, current: T): Partial<T> {
  const delta: Partial<T> = {};

  for (const key in current) {
    if (!Object.hasOwn(current, key)) continue;

    const currentValue = current[key as keyof T];
    const inheritedValue = inherited[key as keyof T];

    // Skip undefined values
    if (currentValue === undefined) continue;

    // If inherited value doesn't exist, include current value
    if (inheritedValue === undefined) {
      delta[key as keyof T] = currentValue;
      continue;
    }

    // Handle nested objects (but not arrays)
    if (
      typeof currentValue === 'object' &&
      currentValue !== null &&
      !Array.isArray(currentValue) &&
      typeof inheritedValue === 'object' &&
      inheritedValue !== null &&
      !Array.isArray(inheritedValue)
    ) {
      const nestedDelta = computeDelta(inheritedValue as object, currentValue as object);
      // Only include if there are differences
      if (Object.keys(nestedDelta).length > 0) {
        delta[key as keyof T] = nestedDelta as T[keyof T];
      }
      continue;
    }

    // For arrays and primitives, compare by JSON stringification
    if (JSON.stringify(currentValue) !== JSON.stringify(inheritedValue)) {
      delta[key as keyof T] = currentValue;
    }
  }

  return delta;
}

/**
 * Deep merge utility for objects
 * Later values override earlier values
 * Skips undefined values; null is treated as an explicit "clear this field" signal
 */
export function deepMerge<T>(target: T, source: Partial<T>): T {
  const output = { ...target } as T;

  for (const key in source) {
    if (Object.hasOwn(source, key)) {
      const sourceValue = source[key];
      const targetValue = (output as Record<string, unknown>)[key];

      // Skip undefined — but allow null (explicit "clear this field")
      if (sourceValue === undefined) {
        continue;
      }

      if (
        sourceValue !== null &&
        typeof sourceValue === 'object' &&
        !Array.isArray(sourceValue) &&
        targetValue &&
        typeof targetValue === 'object' &&
        !Array.isArray(targetValue)
      ) {
        // Recursively merge objects
        (output as Record<string, unknown>)[key] = deepMerge(
          targetValue as T[Extract<keyof T, string>],
          sourceValue as Partial<T[Extract<keyof T, string>]>
        );
      } else {
        // Override with source value (includes null to clear fields)
        (output as Record<string, unknown>)[key] = sourceValue;
      }
    }
  }

  return output;
}

/**
 * Pre-extracted theme defaults from theme.schema.json
 * Source of truth - all defaults must be defined in the JSON Schema
 */
export const THEME_DEFAULTS = {
  theme: getSchemaDefault<'professional' | 'approachable'>(themeSchema, ['theme'])!,
  showGradientSphere: getSchemaDefault<boolean>(themeSchema, ['showGradientSphere'])!,
  defaultLanguage: getSchemaDefault<ThemeLanguage>(themeSchema, ['defaultLanguage'])!,
  colors: {
    brand: getSchemaDefault<string>(themeSchema, ['colors', 'brand'])!,
    buttonBackground: {
      light: getSchemaDefault<string>(themeSchema, ['colors', 'buttonBackground', 'light'])!,
      dark: getSchemaDefault<string>(themeSchema, ['colors', 'buttonBackground', 'dark'])!,
    },
    buttonText: {
      light: getSchemaDefault<string>(themeSchema, ['colors', 'buttonText', 'light'])!,
      dark: getSchemaDefault<string>(themeSchema, ['colors', 'buttonText', 'dark'])!,
    },
    linksColor: {
      light: getSchemaDefault<string>(themeSchema, ['colors', 'linksColor', 'light'])!,
    },
    emailBackground: {
      light: getSchemaDefault<string>(themeSchema, ['colors', 'emailBackground', 'light'])!,
    },
    text: getSchemaDefault<string>(themeSchema, ['colors', 'text'])!,
    muted: getSchemaDefault<string>(themeSchema, ['colors', 'muted'])!,
    border: getSchemaDefault<string>(themeSchema, ['colors', 'border'])!,
    accent: getSchemaDefault<string>(themeSchema, ['colors', 'accent'])!,
  },
  layout: {
    borderRadius: getSchemaDefault<'none' | 'small' | 'medium' | 'large'>(themeSchema, ['layout', 'borderRadius'])!,
  },
} as const;

/**
 * Pre-extracted journey defaults from journey.schema.json
 * Source of truth - all defaults must be defined in the JSON Schema
 */
export const JOURNEY_DEFAULTS = {
  payment: {
    allowPartialPayment: getSchemaDefault<boolean>(journeySchema, ['payment_settings', 'allowPartialPayment'])!,
    enablePaymentPlan: getSchemaDefault<boolean>(journeySchema, ['payment_settings', 'enablePaymentPlan'])!,
    minimumPaymentAmount: getSchemaDefault<number>(journeySchema, ['payment_settings', 'minimumPaymentAmount'])!,
    maximumPaymentAmount: getSchemaDefault<number>(journeySchema, ['payment_settings', 'maximumPaymentAmount'])!,
  },
} as const;
